import * as React from 'react';
import { MatterDetailsTabList } from './MatterDetailsTabList';
import { IconButton, Theme, Tooltip, createStyles, makeStyles } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import EventSystem from '../EventSystem';
import '../App.css';
import { RvMatterGeneralInfoCard } from './general/MatterGeneralInfoCard';
import { RvMatterFinancialsInfoCard } from './financials/FinancialsInfoCard';
import { RvMatterMessageView } from './message/MatterMessageView';
import { RvTimelineInfoView } from './timeline/TimelineInfoView';
// import { MatterDataTopicInfo } from './topic/MatterDataTopicInfo';
import { MatterStaffInfo } from './staff/StaffInfo';
import gql from 'graphql-tag';
import { DataProps, graphql } from 'react-apollo';
import { RecordDetailLayout } from '../v2/layouts/RecordDetailLayout';
import { MatterTerminology } from '../MatterTerminology';
// import { RvDateTimeDisplay } from '../components/DataDisplay/DateTimeDisplay';
import { DateTimeDisplayStyle } from '../components/DataDisplay/DateTimeDisplayStyle';
// import { MatterPartyInfo } from './parties/PartiesInfo';
import { FormattedDateTime } from '../components/DataDisplay/FormattedDateTime';
import { RouterProps, showNotification } from '../App';
import { Parties } from './parties/Parties';
import { Topics } from './topic/Topics';
import { PageNotFound } from '../components/PageNotFound';
import { TabEnumMatter } from '../ClientAndContactDetails/TabEnum';
import { MatterTaskAction } from '../matterSummary/cellRenderer/MatterTaskAction';
import { MatterTaskNotification, UpdateMatterSubscription, UpdateMatterSubscriptionData } from '../matterSummary/MatterSummaryRepository';
import { client } from '..';
import { Subscription } from 'apollo-client/util/Observable';
import { TaskSubscription } from '../tasksSummary/TasksSummaryRepository';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import { DocumentTab } from './documents/DocumentTab';
import { TasksTab } from './tasks/TasksTab';
import { MatterForecastsDrawer } from '../matterSummary/drawers/MatterForecastsDrawer';
import { MatterSummaryContext } from '../matterSummary/MatterSummaryContextProvider';

// Every Matter Details tab's own query now defaults to fetchPolicy:'cache-first' (see each
// tab's own graphql()/useQuery options) rather than the app's global 'cache-and-network', so
// switching tabs re-uses cached data instead of re-fetching every time. This is the escape
// hatch: only the currently-mounted tab has a listener registered at any moment (inactive tabs
// are unmounted - MatterDetailsTabList.tsx only renders `activeTab.content`), so broadcasting
// this refreshes exactly the active tab's data, the same decoupled-broadcast approach
// QuickViewDrawer.tsx's own QUICK_VIEW_REFRESH_EVENT uses for its widgets.
export const MATTER_DETAILS_REFRESH_EVENT = 'matterDetails:refresh';

const useRefreshButtonStyles = makeStyles((theme: Theme) =>
    createStyles({
        refreshIconSpinning: {
            animation: '$spin 700ms linear',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
        },
    })
);

// Same one-shot spin feedback QuickViewDrawer.tsx's own header Refresh button uses - a fixed
// animation on click, not tied to when the refetch(es) it triggers actually resolve (there's
// no single promise to await: the header's own refetch plus whichever tab's listener fires).
const RefreshMatterButton: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
    const classes = useRefreshButtonStyles();
    const [isSpinning, setIsSpinning] = React.useState(false);
    const spinTimeoutRef = React.useRef<number | null>(null);

    const handleClick = () => {
        onRefresh();
        setIsSpinning(true);
        if (spinTimeoutRef.current !== null) {
            window.clearTimeout(spinTimeoutRef.current);
        }
        spinTimeoutRef.current = window.setTimeout(() => setIsSpinning(false), 700);
    };

    React.useEffect(() => () => {
        if (spinTimeoutRef.current !== null) {
            window.clearTimeout(spinTimeoutRef.current);
        }
    }, []);

    return (
        <Tooltip title="Refresh">
            {/* Same size/colour as MatterTaskAction.tsx's own "..." IconButton next to it
                (no size="small", color="primary") so the two header buttons read as a pair. */}
            <IconButton color="primary" onClick={handleClick} aria-label="Refresh matter">
                <RefreshIcon className={isSpinning ? classes.refreshIconSpinning : undefined} />
            </IconButton>
        </Tooltip>
    );
};

interface MatterSection {
  id: number;
  matterId: number;
  isRelationship: boolean;
  sectionName: string;
  description: string;
  longDescription: string;
  sequence: number;
}

export interface MatterOptions {
  canShowTimeline: boolean;
  canShowCriticalDates: boolean;
  canShowMessages: boolean;
  canShowGeneral: boolean;
  canShowDocuments: boolean;
  canShowFinancials: boolean;
  canShowClientFinancials: boolean;
  canShowMatterAccess: boolean;
  canShowStaff: boolean;
  canShowParties: boolean;
  canShowTopics: boolean;
}

export interface ApplicationsLabelSettingsConfig {
  personActingLabel: string;
  personResponsibleLabel: string;
  matterAssistingResponsibilityLabel: string;
  matterIntroducingResponsibilityLabel: string;
  matterWorkingResponsibilityMapping: string;
  matterOversightResponsibilityMapping: string;
  matterAssistingResponsibilityMapping: string;
  matterIntroducerResponsibilityMapping: string;
}
export interface ApplicationsLabelSettings {
  config: ApplicationsLabelSettingsConfig;
}
export interface ApplicationsSettings {
  id: number;
  labelSettings: ApplicationsLabelSettings;
}
export interface Settings {
  id: number;
  applications: ApplicationsSettings;
}

interface MatterProps {
    options: {
      matterOptions: MatterOptions
    };
    matter: {
      id: number;
      matterGuid: string;
      title: string;
      fileNumber: string;
      client: string;
      importanceLevel: number | null;
      sections: {
        recordCount: number;
        sections: Array<MatterSection>;
      };
      lastUpdate: Date;
      status: string;
    };
    tenant: {
      sourceSystem: string;
    };
    appUser: {
        name: string;
        role: string;
        isInternal: boolean;
    }
    settings: Settings;
}
interface MatterDetailsProps extends RouterProps, DataProps<MatterProps, {}> {
  // tslint:disable-next-line: no-any
  location: any;
}

interface MatterDetailsState {
  // tslint:disable-next-line: no-any
  matterSummaryPageXState: any;
  isMatterSummaryDrillDown: boolean;
  // tslint:disable-next-line: no-any
  // clientAndContactState: any;
  nameId?: number;
  // tslint:disable-next-line: no-any
  entityType?: any;
  // tslint:disable-next-line: no-any
  nameSummaryState?: any;
  isSearchCriteriaFromAdvancedFilter?: boolean;
  // passed to the Matter Forecasts drawer as isRefresh; nothing sets it yet (see the commented-out onRefreshHandled below)
  inRefreshData?: boolean;
}

class RvMatterDetails extends React.Component<
  MatterDetailsProps,
  MatterDetailsState
> {

  static contextType = MatterSummaryContext;

  contactFormExists: Boolean = false;
  private matterTaskSubscription: Subscription;
  private taskSubscription: Subscription;
  private updateMatterSubscription: Subscription;
  private isSubscribed: boolean;
  private isBackShow: boolean;

  constructor(props: MatterDetailsProps, state?: MatterDetailsState) {
    super(props, state);
    this.onClickBackBtn = this.onClickBackBtn.bind(this);
    this.activeTabViaRoute = this.activeTabViaRoute.bind(this);
    this.onRefresh = this.onRefresh.bind(this);

    this.isBackShow = true;

    // Explicit bug report: the back button (onClickBackBtn below) only takes the direct
    // "push straight back to /matterSummary" path when this.state.matterSummaryPageXState is
    // set - previously that only ever happened in UNSAFE_componentWillReceiveProps, which
    // doesn't fire on the initial mount. So for the very first matter opened from Matter
    // Summary in a session, this stayed null forever, silently falling through to the
    // history.goBack() branch instead - less reliable than pushing directly back. Seeding the
    // same fields from props.location.state here (mirroring what
    // UNSAFE_componentWillReceiveProps already does for later prop updates) fixes that for the
    // first matter too.
    const initialLocationState = props.location.state;
    this.state = {
      matterSummaryPageXState: initialLocationState?.matterSummaryPageXState ?? null,
      isMatterSummaryDrillDown: initialLocationState
        ? (initialLocationState.isSearchCriteriaFromAdvancedFilter
            ? false
            : initialLocationState.matterSummaryPageXState?.searchCriterias !== undefined)
        : false,
      nameId: initialLocationState?.nameId,
      entityType: initialLocationState?.entityType,
      nameSummaryState: initialLocationState?.nameSummaryState,
      isSearchCriteriaFromAdvancedFilter: initialLocationState?.isSearchCriteriaFromAdvancedFilter
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: MatterDetailsProps) {

    if (sessionStorage.getItem('typeOfView') && sessionStorage.getItem('typeOfView') === 'noback') {
        this.isBackShow = false;
    }

    if (
        this.props.location.state &&
          ( this.state.matterSummaryPageXState !== this.props.location.state.matterSummaryPageXState ||
            this.state.nameId !== this.props.location.state.nameId ||
            this.state.entityType !== this.props.location.state.entityType ||
            this.state.nameSummaryState !== this.props.location.state.nameSummaryState ||
            this.state.isSearchCriteriaFromAdvancedFilter !== this.props.location.state.isSearchCriteriaFromAdvancedFilter
          )
    ) {
      this.setState({
        matterSummaryPageXState: this.props.location.state.matterSummaryPageXState, // State passed in from MatterSummaryPageX
        isMatterSummaryDrillDown: this.props.location.state.isSearchCriteriaFromAdvancedFilter 
                                    ? false 
                                    : this.props.location.state.matterSummaryPageXState.searchCriterias !==  undefined,
        nameId: this.props.location.state.nameId,
        entityType: this.props.location.state.entityType,
        nameSummaryState: this.props.location.state.nameSummaryState
      });
    }
  }

  public componentDidMount() {
    if (!this.isSubscribed) {
        this.isSubscribed = true;
        this.subscribeToMatterTasks();
        this.subscribeToTasks();
        this.subscribeToUpdateMatter();
    }
}

  public componentWillUnmount() {
    if (this.matterTaskSubscription) {
        this.matterTaskSubscription.unsubscribe();
    }

    if (this.taskSubscription) {
      this.taskSubscription.unsubscribe();
    }
    
    if (this.updateMatterSubscription) {
      this.updateMatterSubscription.unsubscribe();
    }
  }

  public render() {
    var matterId = this.props.match.params.id;

    var routeTab = this.props.match.params.tab;

    const { matterGuid, isMatterForcastFormOpen, onMatterForcastFormClose } = this.context;

    if (routeTab === undefined) {
      routeTab = 'generalInformation';
    }

    var sourceSystem = this.props.data.loading
      ? ''
      : this.props.data.tenant?.sourceSystem;

    if (this.props.data.loading) {
      return (
        <RecordDetailLayout title="" isLoading={true}>
          {null}
        </RecordDetailLayout>
      );
    }
    if (!this.props.data.options) {
      return <PageNotFound />;
    }
    var matterOptions = this.props.data.options.matterOptions;

    var appUser = this.props.data.appUser;

    type DetailPage = {
      tabName: string;
      tabKey: number;
      content: {};
      className: string;
      routeName: string;
    };
    var matterSectionViews = new Array<DetailPage>();

    if (matterOptions.canShowStaff) {
      matterSectionViews.push({
        tabName: appUser?.isInternal ? MatterTerminology.InternalResponsibilities : MatterTerminology.ContactFirm,
        tabKey: TabEnumMatter.STAFF,
        content: <MatterStaffInfo id={matterId} labelSettingsConfig={this.props.data.settings?.applications.labelSettings.config} />,
        className: 'contact-firm',
        routeName: 'staff'
      });

      this.contactFormExists = true;
    }

    if (matterOptions.canShowGeneral) {
      matterSectionViews.push({
        tabName: MatterTerminology.GeneralInformation,
        tabKey: TabEnumMatter.GENERALINFORMATION,
        content: (
          <RvMatterGeneralInfoCard
            id={matterId}
            title={MatterTerminology.General}
            options={matterOptions}
          />
        ),
        className: 'general-information',
        routeName: 'generalInformation'
      });
    }
   
    if (matterOptions.canShowFinancials) {
      matterSectionViews.push({
        tabName: MatterTerminology.Financials,
        tabKey: TabEnumMatter.FINANCIALS,
        content: (
          <RvMatterFinancialsInfoCard
            id={matterId}
            title={MatterTerminology.Financials}
            sourceSystem={sourceSystem as string}
            isInternalUser={appUser?.isInternal as boolean}
          />
        ),
        className: 'financials',
        routeName: 'financials'
      });
    }
    if (matterOptions.canShowDocuments && UserFeature.HasAccess(UserFeatureEnum.hasViewMatterDocuments)) {
      matterSectionViews.push({
        tabName: MatterTerminology.Documents,
        tabKey: TabEnumMatter.DOCUMENTS,
        content: <DocumentTab matterId={matterId} />,
        className: 'documents',
        routeName: 'documents'
      });
    }
    if (matterOptions.canShowMessages && UserFeature.HasAccess(UserFeatureEnum.hasMatterNotes)) {
      matterSectionViews.push({
        tabName: MatterTerminology.Messages,
        tabKey: TabEnumMatter.MESSAGES_AND_NOTES,
        content: (
          <RvMatterMessageView id={matterId} sourceSystem={sourceSystem as string} isUserInternal={appUser?.isInternal as boolean} />
        ),
        className: 'messages',
        routeName: 'messages'
      });
    }
    if (UserFeature.HasAccess(UserFeatureEnum.hasViewMatterTask)) {
      matterSectionViews.push({
        tabName: MatterTerminology.Tasks,
        tabKey: TabEnumMatter.TASKS,
        content: <TasksTab matter={this.props.data.matter as any} />,
        className: 'tasks',
        routeName: 'tasks'
      });
    }
    if (matterOptions.canShowTimeline) {
      matterSectionViews.push({
        tabName: MatterTerminology.Timeline,
        tabKey: TabEnumMatter.TIMELINE,
        content: <RvTimelineInfoView id={matterId} />,
        className: 'timeline',
        routeName: 'timeline'
      });
    }
    // topics
    if (matterOptions.canShowTopics) {
      matterSectionViews.push({
        tabName: 'Other Information',
        tabKey: TabEnumMatter.TOPICS,
        content: <Topics matterId={matterId} sourceSystem={sourceSystem as string} />,
        className: 'topics',
        routeName: 'otherInformation'
      });
    }

    // parties
    if (matterOptions.canShowParties) {
      matterSectionViews.push({
        tabName: 'Parties',
        tabKey: TabEnumMatter.PARTIES,
        content: <Parties matterId={matterId} />,
        className: 'parties',
        routeName: 'parties'
      });
    }
    /*
        *   old parties and topic layout
        * 
        if (this.props.data.matter.sections) {
            var sections = this.props.data.matter.sections.sections;
            {sections.map((section, idx) => {
                var  icon: SemanticICONS = section.isRelationship ? 'user outline' : 'file text';
                var  content: JSX.Element = section.isRelationship ?
                (
                    <div>
                        <MatterPartyInfo
                            matterId={matterId}
                            relationshipId={section.id}
                            iconKey="user" 
                            showDescription={true}
                        />
                    </div>
                ) :
                (
                    <div>
                        <MatterDataTopicInfo
                            matterId={matterId}
                            topicId={section.id}
                        />
                    </div>
                ) ;
               
                return matterSectionViews.push({
                    tabName: section.sectionName,
                    iconKey: icon,
                    content: content,
                    className: 'section-name'   
                });
            }); }
        } */
    
    var title = this.props.data.matter?.title || '';
    title = title ? title : MatterTerminology.MatterTitle;

    return (
      <>
        {isMatterForcastFormOpen && matterGuid && matterId && (
            <MatterForecastsDrawer 
                open={isMatterForcastFormOpen}
                onClose={onMatterForcastFormClose}
                matterId={matterId as any}
                matterGuid={matterGuid}
                isRefresh={this.state.inRefreshData}
                // onRefreshHandled={() => this.setState({ inRefreshData: false })}
            />
        )}
        <RecordDetailLayout
          title={title}
          flushBody={true}
          onBack={this.isBackShow ? this.onClickBackBtn : undefined}
          meta={[
            { label: 'File Number', value: this.props.data.matter?.fileNumber },
            { label: 'Status', value: this.props.data.matter?.status, valueColor: 'var(--rv-accent-green)' },
            {
              label: 'Last Updated',
              value: (
                <FormattedDateTime
                  value={this.props.data.matter?.lastUpdate}
                  displayStyle={DateTimeDisplayStyle.ShortDateTime}
                  DisplayInUTC={false}
                />
              ),
            },
          ]}
          actions={(
            <>
              <MatterTaskAction
                key="matterTaskAction"
                matterId={Number(matterId)}
                data={this.props.data.matter}
                hideViewMatter={true}
              />
              <RefreshMatterButton key="refreshMatter" onRefresh={this.onRefresh} />
            </>
          )}
        >
          {matterSectionViews.length > 0 ? (
            <MatterDetailsTabList
              tabs={matterSectionViews}
              activeIndex={(() => {
                const idx = matterSectionViews.findIndex(tab => tab.routeName === routeTab);
                return idx >= 0 ? idx : 0;
              })()}
              matterID={matterId}
            />
          ) : (
            <div />
          )}
        </RecordDetailLayout>
      </>
    );
  }
  private onRefresh() {
    this.props.data.refetch();
    EventSystem.emit(MATTER_DETAILS_REFRESH_EVENT);
  }

  // tslint:disable-next-line:no-any
  private onClickBackBtn(event?: any) {
    // this.props.history.goBack();
    // this.props.history.push('/matterSummary');

    if (this.state.isMatterSummaryDrillDown) {
      this.props.history.push(
        '/matterSummaryDrillDown',
        this.state.matterSummaryPageXState
      );
    } else if (this.state.nameId) {
      this.props.history.goBack();
      // this.props.history.push({
      //   pathname: '/clientAndContactDetail/' + this.state.nameId,
      //   state: { // Some extra state for this location that does not reside in the URL
      //      nameId: this.state.nameId,
      //      entityType: this.state.entityType,
      //      nameSummaryState: this.state.nameSummaryState
      //   }
      // });

    } else if (this.state.matterSummaryPageXState) {
      // this.props.history.goBack();
      this.props.history.push(
        '/matterSummary',
        this.state.matterSummaryPageXState
      );
    } else {
      this.props.history.goBack();
    }

    // if (this.props.location.state) {
    //     this.props.history.push('/matterSummary', this.state.matterSummaryPageXState);
    // } else {
    //     this.props.history.goBack();
    // }

    // browserHistory.push({
    //     pathname: '/matterSummary',
    //     // search: '?the=search', // The URL query string
    //     state: { // Some extra state for this location that does not reside in the URL
    //         matterSummaryPageXState: this.state
    //     }
    // });
  }

  private subscribeToMatterTasks() {      
    this.matterTaskSubscription = client.subscribe({
        query: MatterTaskNotification
    }).subscribe({
        // tslint:disable-next-line:no-any
        next(results: any) {
            // tslint:disable-next-line:no-console
            console.log(results);
            if (results.data.matterManagementTaskNotification.status) {
                showNotification('Success', results.data.matterManagementTaskNotification.message, 'info');
            } else {
                showNotification('Failed', results.data.matterManagementTaskNotification.message, 'error');
            }
        },            
        // tslint:disable-next-line:no-any
        error(err: any) {
            // tslint:disable-next-line:no-console
            console.error(err);
            showNotification(null, err, 'error');
        }
    });
  }

  private subscribeToTasks() {      
    this.taskSubscription = client.subscribe({
        query: TaskSubscription
    }).subscribe({
        // tslint:disable-next-line:no-any
        next(results: any) {
            // tslint:disable-next-line:no-console
            console.log(results);
            if (results.data.taskNotification.status) {
                showNotification('Success', results.data.taskNotification.message, 'info');
            } else {
                showNotification('Failed', results.data.taskNotification.message, 'error');
            }
        },            
        // tslint:disable-next-line:no-any
        error(err: any) {
            // tslint:disable-next-line:no-console
            console.error(err);
            showNotification(null, err, 'error');
        }
    });
  }
  
  private subscribeToUpdateMatter() {
    const vm = this;
    vm.updateMatterSubscription = client
        .subscribe<UpdateMatterSubscriptionData>({
            query: UpdateMatterSubscription,
        })
        .subscribe({
            next(results) {
                if (results.data?.updateMatterNotification.status) {
                    showNotification('Success', results.data.updateMatterNotification.message, 'info');
                    // vm.props.data.refetch()
                } else {
                    showNotification('Failed', results.data?.updateMatterNotification.message ?? '', 'error');
                }
            },
            // tslint:disable-next-line:no-any
            error(err: any) {
                // tslint:disable-next-line:no-console
                console.error(err);
                showNotification(null, err, 'error');
            },
        });
  }

  private activeTabViaRoute(route: string, matterId: string) {
    switch (route) {
      case 'contactFirm': {
        return 0;
      }
      case 'generalInformation': {
        return this.contactFormExists ? 1 : 0;
      }
      case 'financials': {
        return this.contactFormExists ? 2 : 1;
      }
      case 'documents': {
        return this.contactFormExists ? 3 : 2;
      }
      case 'messages': {
        return this.contactFormExists ? 4 : 3;
      }
      case 'timeline': {
        return this.contactFormExists ? 5 : 4;
      }
      case 'otherInformation': {
        return this.contactFormExists ? 6 : 5;
      }
      case 'parties': {
        return this.contactFormExists ? 7 : 6;
      }
      default: {
        return this.contactFormExists ? 1 : 0;
      }
    }
  }
}

const MatterDetailsData = gql`
  query MatterDetailsData($matterId: Int!) {
    options {
      matterOptions(matterId: $matterId) {
        canShowTimeline
        canShowCriticalDates
        canShowMessages
        canShowGeneral
        canShowDocuments
        canShowFinancials
        canShowClientFinancials
        canShowMatterAccess
        canShowStaff
        canShowParties
        canShowTopics
      }
    }
    matter(id: $matterId) {
      id
      matterGuid
      title
      fileNumber
      lastUpdate
      status
      client
      importanceLevel
      sections(includeTopics: true, includeRelationships: true) {
        sections {
          id
          sectionName
          description
          isRelationship
          sequence
        }
      }
    }
    tenant {
      sourceSystem
    }
    appUser {
      name
      role
      isInternal
    }
    settings {
      id
      applications {
        id
        labelSettings {
          config {
            personActingLabel
            personResponsibleLabel
            matterAssistingResponsibilityLabel
            matterIntroducingResponsibilityLabel
            matterWorkingResponsibilityMapping
            matterOversightResponsibilityMapping
            matterAssistingResponsibilityMapping
            matterIntroducerResponsibilityMapping
          }
        }
      }
    }
  }
`;

// tslint:disable-next-line:no-any
const MatterDetails = graphql<any, MatterDetailsProps, any>(MatterDetailsData, {
  options: ({ match }) => ({
    variables: {
      matterId: match.params.id
    },
    // Was inheriting the app's global default (cache-and-network, src/index.tsx) - always
    // re-fetching on mount regardless of cache. Cached after the first load; the header's own
    // Refresh button (onRefresh above) forces a network re-fetch on demand.
    fetchPolicy: 'cache-first'
  })
})(RvMatterDetails);

export default MatterDetails;
